import { User, LogOut, FileText, RotateCcw, Tag, Gift, Shield, ArrowLeftFromLine } from 'lucide-react';
import { Dropdown, DropdownItem, DropdownDivider } from './ui';
import { UserMenuProps, MenuItemConfig, isGiftClaimClaimed } from '../types';
import { SanctumService } from '../services/sanctumService';
import { useUser } from '../contexts/UserContext';
import { usePreviewMode } from '../hooks/usePreviewMode';

/**
 * Get user initials from full name
 * @param fullName - User's full name
 * @returns Two-letter initials (e.g., "John Doe" -> "JD")
 */
function getUserInitials(fullName: string): string {
  if (!fullName) return 'U';

  const nameParts = fullName.trim().split(/\s+/);

  if (nameParts.length === 1) {
    return nameParts[0].charAt(0).toUpperCase();
  }

  return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
}

export function UserMenu({ onShowPromos, onShowGiftClaims, themeColors }: UserMenuProps) {
  const { user, giftClaims } = useUser();
  const { isPreviewMode, disable: exitPreview } = usePreviewMode();

  const menuItems: MenuItemConfig[] = [
    ...(user?.globalAdmin || user?.distributorAdmin || user?.clientAdmin ? [{
      type: 'item' as const,
      icon: <Shield />,
      label: 'Admin',
      href: '/adminpanel',
    }] : []),
    {
      type: 'item',
      icon: <User />,
      label: 'Account',
      href: '/myaccount',
    },
    {
      type: 'item',
      icon: <FileText />,
      label: 'Orders',
      href: '/myorders',
    },
    {
      type: 'item',
      icon: <Tag />,
      label: 'Active Promos',
      onClick: onShowPromos,
    },
    ...(giftClaims.length > 0 ? [{
      type: 'item' as const,
      icon: <Gift />,
      label: 'My Rewards',
      onClick: onShowGiftClaims,
      badge: giftClaims.filter(claim => !isGiftClaimClaimed(claim)).length || undefined,
    }] : []),
    ...(isPreviewMode ? [{
      type: 'item' as const,
      icon: <ArrowLeftFromLine />,
      label: 'Exit Preview',
      onClick: exitPreview,
    }] : []),
    {
      type: 'divider',
    },
    ...(user?.switched ? [{
      type: 'item' as const,
      icon: <RotateCcw />,
      label: 'Switch Back',
      href: '/switch-back',
    }] : []),
    {
      type: 'item',
      icon: <LogOut />,
      label: 'Logout',
      onClick: () => { SanctumService.logout(); },
      variant: 'danger',
    },
  ];

  const userInitials = user ? getUserInitials(user.fullName) : 'U';
  const userName = user?.fullName || 'User';

  const avatarBackground = themeColors?.header_cart_background || '#6366f1';
  const avatarText = themeColors?.header_cart_text_color || '#ffffff';

  const trigger = (
    <button
      className="flex items-center p-1 rounded-full hover:bg-white/10 transition-colors focus:outline-none group"
      aria-label={`User menu - ${userName}`}
      aria-haspopup="true"
      title={userName}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ring-2 ring-white/20 group-hover:ring-white/40 transition-all"
        style={{
          backgroundColor: avatarBackground,
        }}
      >
        <span
          className="text-xs font-bold tracking-wide"
          style={{ color: avatarText }}
          aria-hidden="true"
        >
          {userInitials}
        </span>
      </div>
    </button>
  );

  return (
    <Dropdown trigger={trigger} align="right">
      {/* User name header */}
      <div className="px-4 py-3 border-b border-gray-100">
        <p className="text-sm font-semibold text-gray-800 truncate">{userName}</p>
        {user?.email && (
          <p className="text-xs text-gray-400 truncate mt-0.5">{user.email}</p>
        )}
        {user?.alternateUsername && (
          <p className="text-xs text-gray-400 truncate mt-1">
            <span className="text-gray-500 font-medium">Alt:</span> {user.alternateUsername}
          </p>
        )}
      </div>
      {menuItems.map((item, index) => {
        if (item.type === 'divider') {
          return <DropdownDivider key={`divider-${index}`} />;
        }

        return (
          <DropdownItem
            key={item.label}
            icon={item.icon}
            href={item.href}
            onClick={item.onClick}
            variant={item.variant}
          >
            <span className="flex items-center gap-2">
              {item.label}
              {!!item.badge && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                  {item.badge}
                </span>
              )}
            </span>
          </DropdownItem>
        );
      })}
    </Dropdown>
  );
}
